import * as React from "react";
import { PICKER_PAGE_SIZE, useUserPages, type UserFilters } from "@/lib/queries";
import type { AppUser } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * Finding a person to pick, wherever people are picked (ACL-491): a calendar's staff and host, the
 * calendars' "Manage view" filter, and whose time a block takes.
 *
 * Every one of those once filtered a single fetched page of `GET /app-users`, which stops at 50
 * by email, so anybody after the 50th could be searched for and never found. Here the server
 * searches by name or email, a page at a time, and says how many there are.
 */

export interface UserSearch {
  /** What has loaded so far, in the server's order (by email). */
  users: AppUser[];
  /** How many match in all; null when the server did not say. */
  total: number | null;
  /** A search is in effect: `users` are matches, not simply the first of everyone. */
  searching: boolean;
  /** What `users` were searched for: what was typed, once the debounce has caught up with it. A
   *  picker given this narrows the rows itself until then (`searchedFor` on `Combobox`). */
  searchedFor: string;
  /** The first page for this search is on its way. */
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

/** `query` is what was typed, as typed; it is trimmed and debounced here. */
export function useUserSearch(
  query: string,
  filters: Omit<UserFilters, "q" | "limit" | "offset"> = {},
  options?: { enabled?: boolean },
): UserSearch {
  const typed = useDebounced(query.trim(), 200);
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useUserPages(
    { ...filters, q: typed || undefined },
    { enabled: options?.enabled ?? true },
  );
  const users = React.useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  return {
    users,
    total: data?.pages.at(-1)?.total ?? null,
    searching: typed !== "",
    searchedFor: typed,
    loading: isLoading,
    hasMore: hasNextPage,
    loadingMore: isFetchingNextPage,
    loadMore: () => void fetchNextPage(),
  };
}

/** Under a picker's list: how many of how many, and the next page. Says nothing until loaded. */
export function UserSearchMore({ search, className }: { search: UserSearch; className?: string }) {
  const { users, total, searching, loading, hasMore, loadingMore, loadMore } = search;
  if (loading) return null;
  return (
    <div className={cn("flex items-center justify-between gap-2 text-xs", className)}>
      <span className="text-text-subtle" aria-live="polite">
        {total === null
          ? `${users.length} loaded`
          : `${users.length} of ${total} ${searching ? "matching users" : "users"}`}
      </span>
      {hasMore && (
        <button
          type="button"
          // Keeps the focus in the search box, so the arrows and Enter go on working.
          onMouseDown={(e) => e.preventDefault()}
          onClick={loadMore}
          disabled={loadingMore}
          className="text-accent-11 hover:underline disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : `Load ${PICKER_PAGE_SIZE} more`}
        </button>
      )}
    </div>
  );
}
